/**
 * @fileoverview AR-924 — One uploaded file in the "Files and links" grid: thumbnail, name, meta and remove.
 */

import { useState } from 'react'
import { ActionIcon, Anchor, Box, Center, Group, Paper, Stack, Text } from '@mantine/core'
import { IconFile, IconMusic, IconPhoto, IconTrash, IconVideo } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { formatFileSize } from '../../teacherUpload'
import type { TaskTrackDeliverableFile } from '../../types'

/** Figma: 140×86 cover on the 3-column grid. */
const THUMB_RATIO = '16 / 10'
const GLYPH_SIZE = 28
const TRASH_ICON_SIZE = 16
/** Figma: 10px inset under the thumbnail, 22px trash hit area, so "IMG_2041.jpg" fits on the 3-column grid. */
const CARD_INSET = 'xs'
const TRASH_BUTTON_SIZE = 'sm'
const NAME_TRASH_GAP = 4
const NO_SPACING = 0
const COVER_STYLE = { width: '100%', height: '100%', objectFit: 'cover' as const, display: 'block' }

function TypeGlyph({ fileType }: { fileType: string }) {
  if (fileType === 'video') return <IconVideo size={GLYPH_SIZE} />
  if (fileType === 'audio') return <IconMusic size={GLYPH_SIZE} />
  if (fileType === 'image') return <IconPhoto size={GLYPH_SIZE} />
  return <IconFile size={GLYPH_SIZE} />
}

interface DeliveryFileCardProps {
  readonly file: TaskTrackDeliverableFile
  readonly readOnly: boolean
  readonly removing: boolean
  readonly onRemove: (fileId: string) => void
}

export function DeliveryFileCard({ file, readOnly, removing, onRemove }: DeliveryFileCardProps) {
  const { t } = useTranslation()
  const [coverBroken, setCoverBroken] = useState(false)
  const showCover = Boolean(file.fileCover) && !coverBroken
  const kind = t(`task_tracker.teacher_upload.file_kind_${file.fileType}`, { defaultValue: file.fileType })
  return (
    <Paper withBorder radius="md" sx={{ overflow: 'hidden' }}>
      <Anchor href={file.downloadUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
        <Box sx={theme => ({ aspectRatio: THUMB_RATIO, background: theme.colors.gray[1] })}>
          {showCover ? (
            <img
              src={file.fileCover ?? undefined}
              alt={file.name}
              style={COVER_STYLE}
              onError={() => setCoverBroken(true)}
            />
          ) : (
            <Center sx={theme => ({ height: '100%', color: theme.colors.gray[5] })}>
              <TypeGlyph fileType={file.fileType} />
            </Center>
          )}
        </Box>
      </Anchor>
      <Group position="apart" noWrap px={CARD_INSET} py={CARD_INSET} spacing={NAME_TRASH_GAP} align="center">
        <Stack spacing={NO_SPACING} sx={{ minWidth: 0, flex: 1 }}>
          <Text size="sm" weight={500} lineClamp={1}>{file.name}</Text>
          <Text size="xs" color="dimmed">{[formatFileSize(file.sizeInBytes), kind].filter(Boolean).join(' · ')}</Text>
        </Stack>
        {!readOnly && (
          <ActionIcon
            variant="subtle"
            color="red"
            size={TRASH_BUTTON_SIZE}
            loading={removing}
            onClick={() => onRemove(file.id)}
            aria-label={t('task_tracker.teacher_upload.remove_file')}
          >
            <IconTrash size={TRASH_ICON_SIZE} />
          </ActionIcon>
        )}
      </Group>
    </Paper>
  )
}
